import React, { useContext, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { DashboardContext } from "../contexts/DashboardContext";

export default function EmployeesByTeamChart() {
  const { employeesData } = useContext(DashboardContext);

  // Dynamically calculate team/role distribution from the live backend data
  const teamData = useMemo(() => {
    const data = employeesData || [];

    // Filter to only count Active employees
    const activeEmployees = data.filter(
      (emp) => (emp.status || "").toLowerCase() === "active",
    );
    const totalActive = activeEmployees.length;

    if (totalActive === 0) return null;

    // Count employees per team (Fallback to Role/Remarks if Team is missing)
    const teamCounts = {};
    activeEmployees.forEach((emp) => {
      // Prioritize Team -> then Role/Remarks -> then "Unassigned"
      const category = emp.team || emp.role || emp.remarks || "Unassigned";

      // Filter out strict "N/A" strings so they group into "Unassigned" cleanly
      const finalCategory = category === "N/A" ? "Unassigned" : category;

      teamCounts[finalCategory] = (teamCounts[finalCategory] || 0) + 1;
    });

    // Sort by largest category first
    const sortedTeams = Object.entries(teamCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));

    // Define the colors from your original UI design
    const COLORS = [
      "#3b82f6",
      "#22c55e",
      "#eab308",
      "#a855f7",
      "#94a3b8",
      "#06b6d4",
    ];

    // Keep top 5 categories, group the rest into "Others" so the chart stays clean
    let finalData = [];
    if (sortedTeams.length > 6) {
      finalData = sortedTeams.slice(0, 5);
      const othersCount = sortedTeams
        .slice(5)
        .reduce((acc, curr) => acc + curr.value, 0);
      finalData.push({ name: "Others", value: othersCount });
    } else {
      finalData = sortedTeams;
    }

    // Assign colors and calculate exact percentages
    finalData = finalData.map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length],
      percentage: ((item.value / totalActive) * 100).toFixed(1) + "%",
    }));

    return {
      data: finalData,
      totalActive,
    };
  }, [employeesData]);

  if (!teamData) {
    return (
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col h-[340px] w-full items-center justify-center">
        <p className="text-slate-400 text-sm">No active team data available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col h-[340px] w-full">
      <h3 className="text-sm font-bold text-slate-800 mb-2">
        Employees by Team / Role
      </h3>

      <div className="grid grid-cols-2 items-center flex-1 w-full gap-2">
        {/* Left Side: Fixed Square Donut Container so it never distorts */}
        <div className="relative w-full h-[240px] flex items-center justify-center">
          <PieChart width={150} height={150}>
            <Pie
              data={teamData.data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {teamData.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>

          {/* Centered Donut Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xl font-bold text-slate-900 leading-none">
              {teamData.totalActive}
            </span>
            <span className="text-[11px] text-slate-500 font-medium mt-0.5">
              Active
            </span>
          </div>
        </div>

        {/* Right Side: Clean Legend List */}
        <div className="flex flex-col justify-center gap-2">
          {teamData.data.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between text-[11px]"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                ></span>
                <span
                  className="text-slate-700 font-medium truncate"
                  title={item.name}
                >
                  {item.name}
                </span>
              </div>
              <div className="flex gap-1 text-slate-500 shrink-0 text-right">
                <span className="w-5">{item.value}</span>
                <span className="text-slate-400">({item.percentage})</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
